<template>
  <div class="timeline-wrapper">
    <div
      v-if="showTimes"
      class="hour-markers"
    >
      <div
        v-for="hour in hoursRange"
        :key="hour"
        class="hour-marker"
        :style="{ left: `${hour.position}px` }"
      >
        {{ hour.label }}
      </div>
    </div>
    <div
      v-if="mode != 'summary'"
      class="flex flex-col gap-[2px]"
    >
      <div class="timeline-container block">
        <div
          v-for="(period, index) in computedPresencePeriods"
          :key="index"
          v-tippy="{
            content: `${period.statusTitle} for ${numberFormatter(
              $dayjs.duration(period.duration, 'milliseconds').as('minutes')
            )} min <br>${dayjs(period.start).format('HH:mm')}-${dayjs(period.end).format('HH:mm')}`
          }"
          :style="period.style"
          class="timeline-period"
        >
          <Icon
            v-if="parseFloat(period.style.width) > 10"
            :type="period.icon"
            fa-style="far"
            class="text-xs text-white opacity-80"
          />
        </div>
      </div>
      <div class="timeline-container block">
        <div
          v-for="(period, index) in computedStatusPeriods"
          :key="index"
          v-tippy="{
            content: `${period.statusTitle} for ${numberFormatter(
              $dayjs.duration(period.duration, 'milliseconds').as('minutes')
            )} min <br>${dayjs(period.start).format('HH:mm')}-${dayjs(period.end).format('HH:mm')}`
          }"
          :style="period.style"
          class="timeline-period"
        >
          <Icon
            v-if="parseFloat(period.style.width) > 10"
            :type="period.icon"
            fa-style="far"
            class="text-xs text-white opacity-80"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex flex-col gap-[2px]"
    >
      <div class="timeline-container flex">
        <div
          v-for="(status, index) in computedSummarizedPresencePeriods"
          :key="index"
          v-tippy="{
            content: `${status.label} for ${numberFormatter($dayjs.duration(status.duration, 'milliseconds').as('minutes'))} min`
          }"
          :style="status.style"
          class="timeline-period"
        >
          <Icon
            v-if="parseFloat(status.style.width) > 1"
            :type="status.icon"
            fa-style="far"
            class="text-sm text-white opacity-80"
          />
        </div>
      </div>
      <div class="timeline-container flex">
        <div
          v-for="(status, index) in computedSummarizedStatusPeriods"
          :key="index"
          v-tippy="{
            content: `${status.label} for ${numberFormatter($dayjs.duration(status.duration, 'milliseconds').as('minutes'))} min`
          }"
          :style="status.style"
          class="timeline-period"
        >
          <Icon
            v-if="parseFloat(status.style.width) > 1"
            :type="status.icon"
            fa-style="far"
            class="text-sm text-white opacity-80"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// vue
import { computed, ref, watch } from 'vue'

// components
import Icon from '@/components/icon/Icon.vue'

// utilities
import { toPercent } from '@/utilities'
const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  },
  mode: {
    type: String,
    default: 'compact' // compact, relative, summary
  },
  startTime: {
    type: String,
    default: '09:00'
  },
  endTime: {
    type: String,
    default: '16:00'
  },
  showTimes: {
    type: Boolean,
    default: false
  },
  scale: {
    type: Number,
    default: 2 // Default scale: 1 pixel per minute
  }
})
const PIXELS_PER_MINUTE = ref(props.scale)

// Watch for changes in the scale prop and update PIXELS_PER_MINUTE
watch(
  () => props.scale,
  newScale => {
    PIXELS_PER_MINUTE.value = newScale
  }
)

function getStatusDetails(status) {
  let details = {}
  switch (status) {
    case 'online':
      details = {
        order: 1,
        icon: 'check',
        label: 'Online',
        color: 'var(--bg-success-solid)'
      }
      break
    case 'available':
      details = {
        order: 2,
        icon: 'check',
        label: 'Available',
        color: 'var(--bg-success-solid)'
      }
      break
    case 'offline':
      details = {
        order: 9,
        icon: 'circle-x',
        label: 'Offline',
        color: 'var(--bg-error-solid)'
      }
      break
    case 'unavailable':
      details = {
        order: 10,
        icon: 'ban',
        label: 'Unavailable',
        color: 'var(--bg-secondary)'
      }
      break
    case 'idle':
      details = {
        order: 3,
        icon: 'zzz',
        label: 'Idle',
        color: 'var(--bg-warning-solid)'
      }
      break
    case 'on-call':
      details = {
        order: 5,
        icon: 'phone',
        label: 'In Call',
        color: 'var(--bg-info-solid)'
      }
      break
    case 'break':
      details = {
        order: 7,
        icon: 'mug',
        label: 'On Break',
        color: 'var(--bg-error-solid)'
      }
      break
    default:
      details = {
        order: 11,
        icon: 'fa-user',
        label: 'Unknown',
        color: 'var(--bg-secondary)'
      }
  }

  return details
}

const computedSummarizedPresencePeriods = computed(() => {
  const presencePeriods = Array.isArray(props.data.presenceLogs) ? props.data.presenceLogs : []
  return presencePeriods ? summarizePeriods(presencePeriods) : presencePeriods
})

const computedSummarizedStatusPeriods = computed(() => {
  const statusPeriods = Array.isArray(props.data.callAgentStatuses) ? props.data.callAgentStatuses : []
  return statusPeriods ? summarizePeriods(statusPeriods) : statusPeriods
})

const summarizePeriods = periods => {
  const statuses = []

  if (periods.length > 0) {
    periods[periods.length - 1].end = periods[periods.length - 1].start
  }

  periods.forEach(period => {
    const duration = new Date(period.end).getTime() - new Date(period.start).getTime()
    const statusDetails = getStatusDetails(period.status)

    const statusIndex = statuses.findIndex(status => status.label === statusDetails.label)
    if (statusIndex === -1) {
      statuses.push({
        label: statusDetails.label,
        order: statusDetails.order,
        style: {
          backgroundColor: statusDetails.color,
          width: '0%'
        },
        icon: statusDetails.icon,
        duration: duration,
        start: period.start,
        end: period.end
      })
    } else {
      statuses[statusIndex].duration += duration
      statuses[statusIndex].end = period.end
    }
  })

  const totalDuration = statuses.reduce((acc, status) => acc + status.duration, 0)
  statuses.forEach(status => {
    status.style.width = toPercent(status.duration / totalDuration, 2)
  })

  const orderedItems = statuses.sort((a, b) => a.order - b.order)

  return orderedItems
}

const computedPresencePeriods = computed(() => {
  const presencePeriods = Array.isArray(props.data.presenceLogs) ? props.data.presenceLogs : []
  return presencePeriods ? orderPeriods(presencePeriods) : presencePeriods
})

const computedStatusPeriods = computed(() => {
  const statusPeriods = Array.isArray(props.data.callAgentStatuses) ? props.data.callAgentStatuses : []

  return statusPeriods ? orderPeriods(statusPeriods) : statusPeriods
})

/**
 * Orders periods and calculates their positioning on the timeline
 * @param {Array} periods - Array of period objects with start, end, and status properties
 * @returns {Array} Array of period objects with calculated style properties
 */
const orderPeriods = periods => {
  if (!periods || periods.length === 0) {
    return []
  }

  // Extract day context from the first period's start date
  // Handle both ISO format (2024-01-01T08:00:00Z) and space-separated format (2024-01-01 08:00:00)
  let dayContext = '1970-01-01'
  if (periods[0] && periods[0].start) {
    const firstPeriodStart = periods[0].start
    if (typeof firstPeriodStart === 'string') {
      // Try ISO format first (contains 'T')
      if (firstPeriodStart.includes('T')) {
        dayContext = firstPeriodStart.split('T')[0]
      } else if (firstPeriodStart.includes(' ')) {
        // Fallback to space-separated format
        dayContext = firstPeriodStart.split(' ')[0]
      } else {
        // If it's just a date string, use it directly
        dayContext = firstPeriodStart.substring(0, 10)
      }
    } else if (firstPeriodStart instanceof Date) {
      // If it's already a Date object, format it
      dayContext = firstPeriodStart.toISOString().split('T')[0]
    }
  }

  // Calculate timeline start time
  const timelineStartDate = new Date(`${dayContext}T${props.startTime}:00`)
  const timelineStart = timelineStartDate.getTime()

  // Validate timeline start
  if (isNaN(timelineStart)) {
    console.error('Invalid timeline start date:', `${dayContext}T${props.startTime}:00`)
    return periods.map(period => ({
      ...period,
      style: {
        position: 'absolute',
        left: '0px',
        width: '0px',
        backgroundColor: getStatusDetails(period.status).color
      },
      statusTitle: getStatusDetails(period.status).label,
      icon: getStatusDetails(period.status).icon,
      duration: 0
    }))
  }

  return periods.map(period => {
    // Parse period start and end dates
    const periodStartDate = new Date(period.start)
    const periodEndDate = new Date(period.end)
    const periodStart = periodStartDate.getTime()
    const periodEnd = periodEndDate.getTime()

    // Validate dates
    if (isNaN(periodStart) || isNaN(periodEnd)) {
      console.warn('Invalid period date:', { start: period.start, end: period.end })
      return {
        ...period,
        style: {
          position: 'absolute',
          left: '0px',
          width: '0px',
          backgroundColor: getStatusDetails(period.status).color
        },
        statusTitle: getStatusDetails(period.status).label,
        icon: getStatusDetails(period.status).icon,
        duration: 0
      }
    }

    const duration = periodEnd - periodStart
    const offset = periodStart - timelineStart

    // Calculate width and left offset in pixels
    const widthPixels = (duration / 60000) * PIXELS_PER_MINUTE.value
    const leftOffsetPixels = (offset / 60000) * PIXELS_PER_MINUTE.value

    // Ensure non-negative values
    const validWidth = isNaN(widthPixels) || widthPixels < 0 ? 0 : widthPixels
    const validLeft = isNaN(leftOffsetPixels) || leftOffsetPixels < 0 ? 0 : leftOffsetPixels

    const statusDetails = getStatusDetails(period.status)

    return {
      ...period,
      style: {
        position: 'absolute',
        left: `${validLeft}px`,
        width: `${validWidth}px`,
        backgroundColor: statusDetails.color
      },
      statusTitle: statusDetails.label,
      icon: statusDetails.icon,
      duration: duration
    }
  })
}

const hoursRange = computed(() => {
  const range = []
  const [startHour] = props.startTime.split(':').map(Number)
  const [endHour] = props.endTime.split(':').map(Number)

  let totalHours = endHour - startHour
  if (totalHours < 0) {
    totalHours += 24
  }

  for (let i = 0; i <= totalHours; i++) {
    const currentHour = (startHour + i) % 24
    const position = i * 60 * PIXELS_PER_MINUTE.value

    let label = `${currentHour < 10 ? '0' : ''}${currentHour}:00`
    range.push({
      label: label,
      position: position
    })

    if (i < totalHours) {
      label = `${currentHour < 10 ? '0' : ''}${currentHour}:30`
      range.push({
        label: label,
        position: position + 30 * PIXELS_PER_MINUTE.value
      })
    }
  }

  return range
})
</script>

<style>
.timeline-wrapper {
  position: relative;
  overflow-x: visible;
  white-space: nowrap; /* Prevent wrapping of child elements */
}

.hour-markers {
  /* position: absolute; */
  width: 100%;
  height: 20px;
  top: 0;
  pointer-events: none; /* Allows clicks to pass through */
}

.hour-marker {
  position: absolute;
  transform: translateX(-50%);
  pointer-events: auto; /* Enable interaction, if needed */
  font-size: var(--text-xs);
}
.hour-marker::before {
  content: '';
  position: absolute;
  /* top: 0; */
  left: 50%;
  bottom: -5px;
  width: 1px;
  height: 6px;
  background-color: #535353;
}

.hour-marker:first-child {
  transform: translateX(0%); /* Adjusts so it's not cut off */
}
.hour-marker:first-child::before {
  left: 0;
}

.hour-marker:last-child {
  transform: translateX(-100%); /* Adjusts so it's not cut off */
}
.hour-marker:last-child::before {
  left: 95%;
}

.timeline-container {
  position: relative; /* Allows absolute positioning within */
  min-width: 100%;
  height: 15px; /* Adjust based on your design */
  background-color: #efefef;
}
.dark .timeline-container {
  background-color: var(--darkmode-2);
}

.timeline-period {
  height: 100%; /* This ensures each period has height and is visible */
  display: flex; /* Optional, depending on your design */
  align-items: center; /* Optional, centers content vertically */
  justify-content: center; /* Optional, centers content horizontally */
  box-sizing: border-box; /* Ensures padding and borders are included in the width */
  position: relative; /* Needed if you're positioning icons or other elements absolutely */
  border-right: 1px solid var(--utility-gray-50);
}

/* Additional styles for periods based on their status can go here */
</style>
